'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, RefreshCw, LogOut } from 'lucide-react';

interface AppLayoutProps {
  children: React.ReactNode;
  activePath: string;
}

function EmailVerificationGate() {
  const { user, signOut, resendVerificationEmail } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex h-screen bg-background items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center mx-auto mb-6">
          <Mail size={28} className="text-primary" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">Verify your email to continue</h1>
        <p className="text-sm text-muted-foreground mb-2">
          We sent a verification link to{' '}
          <span className="text-foreground font-semibold">{user?.email}</span>.
        </p>
        <p className="text-xs text-muted-foreground mb-6">
          Click the link in that email to activate your account. Check your spam folder if you don&apos;t see it.
        </p>

        {resent && (
          <div className="mb-4 px-4 py-2.5 rounded-lg bg-green-500/10 border border-green-500/25 text-green-400 text-xs font-medium">
            ✓ Verification email resent — check your inbox.
          </div>
        )}
        {error && (
          <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-400 text-xs">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Mail size={14} />
            )}
            {resending ? 'Sending…' : resent ? 'Email sent ✓' : 'Resend verification email'}
          </button>

          <button
            onClick={() => signOut()}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-secondary border border-border text-muted-foreground rounded-xl text-sm font-medium hover:text-foreground transition-all duration-150 active:scale-95"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>

        <p className="mt-6 text-xs text-muted-foreground">
          Already verified?{' '}
          <button
            onClick={() => window.location.reload()}
            className="text-primary hover:underline"
          >
            Refresh the page
          </button>
        </p>
      </div>
    </div>
  );
}

export default function AppLayout({ children, activePath }: AppLayoutProps) {
  const { user, loading, emailVerified } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/sign-up-login');
    }
  }, [loading, user, router]);

  // While auth is loading, render nothing (avoids flash)
  if (loading) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Not logged in — show nothing while redirect happens
  if (!user) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Logged in but email not verified — show gate
  if (user && !emailVerified) {
    return <EmailVerificationGate />;
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar activePath={activePath} />
      <main className="flex-1 overflow-y-auto scrollbar-thin">
        {children}
      </main>
    </div>
  );
}